import React from "react";
import { Modal } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import TrustedPersonsForm from "~/pages/Editor/EditorTrustedPersons/modals/TrustedPersonsForm";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getNumberYearMonthDay } from "~/libs/getDateOptions";
import {
  TrustedPerson,
  useCreateTrustedPersonMutation,
  useUpdateTrustedPersonMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

const formId = "TrustedPersonsForm";

interface TrustedPersonsModalProps {
  reFetch: () => void;
  currentItem?: TrustedPerson;
}

const TrustedPersonsModal = ({
  reFetch,
  currentItem,
}: TrustedPersonsModalProps) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useGetLocationData();
  const setData = useSetLocationData(true);
  const isOpenCreateModal = openModal === "Create";
  const isOpenEditModal = openModal === "Edit";

  const { notifySuccess, notifyError } = useNotify();

  // Handlers
  const closeModal = () => setData("openModal", "", "currentId", "");

  const [setTrustedPerson] = useCreateTrustedPersonMutation({
    onCompleted: () => {
      reFetch();
      notifySuccess(t("trustedPersons.editSuccess"));
      closeModal();
    },
    onError: (error) => {
      notifyError(error.message);
    },
  });

  const [updateTrustedPersons] = useUpdateTrustedPersonMutation({
    onCompleted: () => {
      reFetch();
      notifySuccess(t("trustedPersons.editSuccess"));
      closeModal();
    },
  });
  const onSubmit = (data): void => {
    if (currentId) {
      updateTrustedPersons({
        variables: {
          updateTrustedPersonId: currentId,
          powerOfAttorney: data.powerOfAttorney,
          fio: data.fio,
          shortFio: data.shortFio,
          descriptionContract: data.descriptionContract,
          active: data.active.value === "active",
          date: getNumberYearMonthDay({ date: data.date, separator: "-" }),
          endDate: getNumberYearMonthDay({
            date: data.endDate,
            separator: "-",
          }),
        },
      });
    } else {
      setTrustedPerson({
        variables: {
          powerOfAttorney: data.powerOfAttorney,
          fio: data.fio,
          shortFio: data.shortFio,
          descriptionContract: data.descriptionContract,
          active: data.active.value === "active",
          date: getNumberYearMonthDay({ date: data.date, separator: "-" }),
          endDate: getNumberYearMonthDay({
            date: data.endDate,
            separator: "-",
          }),
        },
      });
    }
  };

  return (
    <Modal
      title={t("trustedPersons.confidant")}
      open={isOpenCreateModal || isOpenEditModal}
      onCancel={closeModal}
      closable
      centered
      destroyOnHidden
      okButton={{
        htmlType: "submit",
        form: formId,
        children: t("ui.save"),
      }}
    >
      <TrustedPersonsForm
        currentItem={isOpenEditModal ? currentItem : undefined}
        onSubmit={onSubmit}
        formId={formId}
      />
    </Modal>
  );
};

export default TrustedPersonsModal;
